import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { referenceService } from '@/services/referenceService';
import { Von00Perinatal, Von01Maternal } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const ABROAD_OPTION = 'Străinătate';

interface Von00PerinatalFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von00PerinatalForm: React.FC<Von00PerinatalFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);

  // Form fields
  const [sex, setSex] = useState<string>('');
  const [gaDays, setGaDays] = useState<string>('');
  const [gaWeeks, setGaWeeks] = useState<string>('');
  const [birthweightG, setBirthweightG] = useState<string>('');
  const [headCircumference, setHeadCircumference] = useState<string>('');
  const [diedFirst12hours, setDiedFirst12hours] = useState(false);

  // Birth/transfer fields are stored in the maternal section (von_01_maternal)
  const [hasMaternalRecord, setHasMaternalRecord] = useState(false);
  const [matAutoFilled, setMatAutoFilled] = useState<string[]>([]);
  const [birthDatetime, setBirthDatetime] = useState<string>('');
  const [admitDatetime, setAdmitDatetime] = useState<string>('');
  const [diedDr, setDiedDr] = useState(false);
  const [transferFrom, setTransferFrom] = useState<string>('');
  const [transferTo, setTransferTo] = useState<string>('');
  const [transferFromDetails, setTransferFromDetails] = useState<string>('');
  const [transferToDetails, setTransferToDetails] = useState<string>('');
  const [hospitalNames, setHospitalNames] = useState<string[]>([]);
  const [transferFromReason, setTransferFromReason] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [data, maternal, hospitals] = await Promise.all([
        vonDataService.getPerinatal(patientId),
        vonDataService.getMaternal(patientId),
        referenceService.getHospitals()
      ]);
      setHospitalNames([...hospitals.map((hospital) => hospital.hospital_name), ABROAD_OPTION]);

      if (maternal) {
        setHasMaternalRecord(Boolean(maternal.id));
        setMatAutoFilled(Array.isArray(maternal._autofilled) ? maternal._autofilled : []);
        setBirthDatetime(maternal.birth_datetime || '');
        setAdmitDatetime(maternal.admit_datetime || '');
        setDiedDr(Boolean(maternal.died_dr));
        setTransferFrom(maternal.transfer_from || '');
        setTransferTo(maternal.transfer_to || '');
        setTransferFromDetails(maternal.transfer_from_details || '');
        setTransferToDetails(maternal.transfer_to_details || '');
        setTransferFromReason(maternal.transfer_from_reason?.toString() || '');
      }

      if (data) {
        // Check if data has ID (existing record) or is auto-filled from patient
        const isExisting = Boolean(data.id);
        setHasExistingData(isExisting);

        // Track auto-filled fields
        if (data._autofilled && Array.isArray(data._autofilled)) {
          setAutoFilledFields(data._autofilled);
        } else {
          setAutoFilledFields([]);
        }

        setSex(data.sex?.toString() || '');
        setGaDays(data.ga_days?.toString() || '');
        setGaWeeks(data.ga_weeks?.toString() || '');
        setBirthweightG(data.birthweight_g?.toString() || '');
        setHeadCircumference(data.head_circumference?.toString() || '');
        setDiedFirst12hours(Boolean(data.died_first_12hours));
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von00Perinatal> = {
        patient_id: patientId,
        // Only include fields that are NOT auto-filled from patient data
        sex: autoFilledFields.includes('sex') ? undefined : (sex ? parseInt(sex) : undefined),
        ga_days: gaDays ? parseInt(gaDays) : undefined,
        ga_weeks: autoFilledFields.includes('ga_weeks') ? undefined : (gaWeeks ? parseInt(gaWeeks) : undefined),
        birthweight_g: autoFilledFields.includes('birthweight_g') ? undefined : (birthweightG ? parseInt(birthweightG) : undefined),
        head_circumference: headCircumference ? parseFloat(headCircumference) : undefined,
        died_first_12hours: diedFirst12hours
      };

      const maternalData: Partial<Von01Maternal> = {
        patient_id: patientId,
        birth_datetime: matAutoFilled.includes('birth_datetime') ? undefined : (birthDatetime || undefined),
        admit_datetime: admitDatetime || undefined,
        died_dr: diedDr,
        transfer_from: matAutoFilled.includes('transfer_from') ? undefined : (transferFrom || undefined),
        transfer_to: transferTo || undefined,
        transfer_from_details: transferFrom === ABROAD_OPTION ? transferFromDetails : '',
        transfer_to_details: transferTo === ABROAD_OPTION ? transferToDetails : '',
        transfer_from_reason: transferFromReason ? parseInt(transferFromReason) : undefined
      };

      if (hasMaternalRecord) {
        await vonDataService.updateMaternal(patientId, maternalData);
      } else {
        await vonDataService.createMaternal(patientId, maternalData);
        setHasMaternalRecord(true);
      }

      if (hasExistingData) {
        await vonDataService.updatePerinatal(patientId, formData);
      } else {
        await vonDataService.createPerinatal(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 00: Informații generale</CardTitle>
        <CardDescription>Date perinatale de bază</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="birthDatetime" className="flex items-center gap-2">
                Data și ora nașterii
                {matAutoFilled.includes('birth_datetime') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completat automat din date pacient</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Label>
              <Input
                id="birthDatetime"
                type="datetime-local"
                value={birthDatetime}
                onChange={(e) => setBirthDatetime(e.target.value)}
                disabled={readOnly || matAutoFilled.includes('birth_datetime')}
                className={matAutoFilled.includes('birth_datetime') ? 'border-blue-400 bg-blue-50' : ''}
              />
            </div>

            {/* Sex */}
            <div className="space-y-2">
              <Label htmlFor="sex" className="flex items-center gap-2">
                Sex copil
                {autoFilledFields.includes('sex') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completat automat din date pacient</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Label>
              <Select value={sex} onValueChange={setSex} disabled={readOnly || autoFilledFields.includes('sex')}>
                <SelectTrigger id="sex" className={autoFilledFields.includes('sex') ? 'border-blue-400 bg-blue-50' : ''}>
                  <SelectValue placeholder="Selectează..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Masculin</SelectItem>
                  <SelectItem value="2">Feminin</SelectItem>
                  <SelectItem value="9">Necunoscut</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Gestational age - weeks */}
            <div className="space-y-2">
              <Label htmlFor="gaWeeks" className="flex items-center gap-2">
                Vârsta gestațională (săptămâni)
                {autoFilledFields.includes('ga_weeks') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completat automat din date pacient</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Label>
              <Input
                id="gaWeeks"
                type="number"
                min="20"
                max="36"
                value={gaWeeks}
                onChange={(e) => setGaWeeks(e.target.value)}
                placeholder="20-36"
                disabled={readOnly || autoFilledFields.includes('ga_weeks')}
                className={autoFilledFields.includes('ga_weeks') ? 'border-blue-400 bg-blue-50' : ''}
              />
            </div>

            {/* Gestational age - days */}
            <div className="space-y-2">
              <Label htmlFor="gaDays">Vârsta gestațională (zile)</Label>
              <Input
                id="gaDays"
                type="number"
                min="0"
                value={gaDays}
                onChange={(e) => setGaDays(e.target.value)}
                placeholder="zile"
                disabled={readOnly}
              />
            </div>

            {/* Birth weight */}
            <div className="space-y-2">
              <Label htmlFor="birthweight" className="flex items-center gap-2">
                Greutate la naștere (grame)
                {autoFilledFields.includes('birthweight_g') && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completat automat din date pacient</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Label>
              <Input
                id="birthweight"
                type="number"
                min="300"
                max="6000"
                value={birthweightG}
                onChange={(e) => setBirthweightG(e.target.value)}
                placeholder="300-6000"
                disabled={readOnly || autoFilledFields.includes('birthweight_g')}
                className={autoFilledFields.includes('birthweight_g') ? 'border-blue-400 bg-blue-50' : ''}
              />
            </div>

            {/* Head circumference */}
            <div className="space-y-2">
              <Label htmlFor="headCirc">Circumferință craniană (cm)</Label>
              <Input
                id="headCirc"
                type="number"
                step="0.1"
                value={headCircumference}
                onChange={(e) => setHeadCircumference(e.target.value)}
                placeholder="cm"
                disabled={readOnly}
              />
            </div>

              <div className="space-y-2">
                <Label htmlFor="admitDatetime">Data și ora internării în NICU</Label>
                <Input
                  id="admitDatetime"
                  type="datetime-local"
                  value={admitDatetime}
                  onChange={(e) => setAdmitDatetime(e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="transferFrom" className="flex items-center gap-2">
                  Transfer de la centru
                  {matAutoFilled.includes('transfer_from') && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-blue-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Completat automat din date pacient</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </Label>
                <SearchableSelect
                  id="transferFrom"
                  options={hospitalNames}
                  value={transferFrom}
                  onChange={setTransferFrom}
                  placeholder="Selectează spitalul..."
                  searchPlaceholder="Caută spital sau maternitate..."
                  disabled={readOnly || matAutoFilled.includes('transfer_from')}
                  className={matAutoFilled.includes('transfer_from') ? 'border-blue-400 bg-blue-50' : undefined}
                />
              </div>

              {transferFrom === ABROAD_OPTION && (
                <div className="space-y-2">
                  <Label htmlFor="transferFromDetails">Detalii străinătate</Label>
                  <Input
                    id="transferFromDetails"
                    value={transferFromDetails}
                    onChange={(e) => setTransferFromDetails(e.target.value)}
                    placeholder="Țara, spitalul"
                    disabled={readOnly}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="transferTo">Transfer către centru</Label>
                <SearchableSelect
                  id="transferTo"
                  options={hospitalNames}
                  value={transferTo}
                  onChange={setTransferTo}
                  placeholder="Selectează spitalul..."
                  searchPlaceholder="Caută spital sau maternitate..."
                  disabled={readOnly}
                />
              </div>

              {transferTo === ABROAD_OPTION && (
                <div className="space-y-2">
                  <Label htmlFor="transferToDetails">Detalii străinătate</Label>
                  <Input
                    id="transferToDetails"
                    value={transferToDetails}
                    onChange={(e) => setTransferToDetails(e.target.value)}
                    placeholder="Țara, spitalul"
                    disabled={readOnly}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="transferReason">Motiv transfer</Label>
                <Select value={transferFromReason} onValueChange={setTransferFromReason} disabled={readOnly}>
                  <SelectTrigger id="transferReason">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Medical</SelectItem>
                    <SelectItem value="4">Chirurgical</SelectItem>
                    <SelectItem value="7">Hipotermie</SelectItem>
                    <SelectItem value="5">Cronic</SelectItem>
                    <SelectItem value="1">ECMO</SelectItem>
                    <SelectItem value="8">CRRT</SelectItem>
                    <SelectItem value="6">Altele</SelectItem>
                  </SelectContent>
                </Select>
              </div>
          </div>

          {/* Died in delivery room */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="diedDr"
              checked={diedDr}
              onCheckedChange={(checked) => setDiedDr(checked === true)}
              disabled={readOnly}
            />
            <Label htmlFor="diedDr" className="text-sm font-normal cursor-pointer">
              Decedat în sala de naștere
            </Label>
          </div>

          {/* Died within 12 hours */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="died12h"
              checked={diedFirst12hours}
              onCheckedChange={(checked) => setDiedFirst12hours(checked === true)}
              disabled={readOnly}
            />
            <Label
              htmlFor="died12h"
              className="text-sm font-normal cursor-pointer"
            >
              Decedat în primele 12 ore
            </Label>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
